/* product-variant-sync.js  – expects `window.productData` to be populated beforehand */

document.addEventListener('DOMContentLoaded', () => {
  if (!window.productData || !window.productData.variants?.length) {
    return;
  }

  const { optionNames, variants } = window.productData;

  /* ---------- helpers ---------- */
  const getIdx = label =>
    optionNames.findIndex(o => o.toLowerCase() === label.toLowerCase());

  const sizeIdx  = getIdx('size');
  const colorIdx = getIdx('color');
  
  // For products without color/size options, use the first option as the media-triggering option
  const firstOptionIdx = 0;

  // Color mapping to handle UI display names vs actual variant option names
  const mapColorName = (uiColorName) => {
    const colorMap = {
      'White/Pink': 'Light Pink',
      'White Pink': 'Light Pink',
      'White-Pink': 'Light Pink'
    };
    return colorMap[uiColorName] || uiColorName;
  };

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

  // Format price using theme money format (without currency code)
  const formatMoney = (cents) => {
    if (!theme?.settings?.moneyFormat || cents === null || cents === undefined) {
      return `$${(cents / 100).toFixed(2)}`;
    }
    
    const moneyString = (cents / 100).toFixed(2);
    return theme.settings.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, moneyString);
  };

  // Update price display when variant changes
  const updatePriceDisplay = (variant) => {
    if (!variant) return;

    const priceContainer = $('.price');
    if (!priceContainer) return;

    // Update main price
    const currentPriceElement = priceContainer.querySelector('.price__default .strong');
    if (currentPriceElement) {
      currentPriceElement.innerHTML = formatMoney(variant.price);
    }

    // Update compare-at price
    const compareAtPriceElement = priceContainer.querySelector('.price__default .price__was');
    if (compareAtPriceElement) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareAtPriceElement.innerHTML = formatMoney(variant.compare_at_price);
        compareAtPriceElement.style.display = '';
      } else {
        compareAtPriceElement.innerHTML = '';
        compareAtPriceElement.style.display = 'none';
      }
    }

    // Update unit price
    const unitPriceContainer = priceContainer.querySelector('.unit-price');
    if (unitPriceContainer && variant.unit_price_measurement) {
      const unitPriceElement = unitPriceContainer.querySelector('.unit-price__price');
      const unitElement = unitPriceContainer.querySelector('.unit-price__unit');
      
      if (unitPriceElement && variant.unit_price) {
        unitPriceElement.innerHTML = formatMoney(variant.unit_price);
      }
      
      if (unitElement && variant.unit_price_measurement) {
        const referenceValue = variant.unit_price_measurement.reference_value !== 1 
          ? variant.unit_price_measurement.reference_value 
          : '';
        unitElement.innerHTML = referenceValue + variant.unit_price_measurement.reference_unit;
      }
      
      unitPriceContainer.hidden = !variant.unit_price_measurement;
    } else if (unitPriceContainer) {
      unitPriceContainer.hidden = true;
    }

    // Update price container classes
    priceContainer.classList.toggle('price--on-sale', variant.compare_at_price && variant.compare_at_price > variant.price);
    priceContainer.classList.toggle('price--sold-out', !variant.available);
  };

  /* ---------- core ---------- */
  const findVariant = () => {
    // Get currently selected variant ID from hidden input
    const currentId = $('input[name="id"]')?.value;
    if (currentId) {
      const variant = variants.find(v => v.id == currentId);
      if (variant) {
        return variant;
      }
    }

    // Fallback to first available variant
    const firstAvailable = variants.find(v => v.available) || variants[0];
    return firstAvailable;
  };

  const applyVariant = (v, isMediaChange = false) => {
    if (!v) return false;

    // Find the specific form for this product instance
    // First try to find the form that contains our currently selected radio button
    const selectedRadio = $('input[type="radio"]:checked[data-option-name]');
    let targetForm = selectedRadio?.closest('form[action*="/cart/add"]');
    
    // If radio buttons are outside forms, find the form that should handle this product
    if (!targetForm && window.productData?.id) {
      const formWithProductId = $$('form[action*="/cart/add"]').find(form => {
        const formProductId = form.querySelector('input[name="product-id"]')?.value || 
                             form.querySelector('[data-product-id]')?.dataset.productId;
        return formProductId == window.productData.id;
      });
      targetForm = formWithProductId;
    }
    
    // Final fallback - for featured products, try to find the form closest to where radio buttons are
    if (!targetForm && selectedRadio) {
      // Look for the nearest form in the DOM tree
      const nearestSection = selectedRadio.closest('.featured-product, .product, .cc-featured-product');
      if (nearestSection) {
        targetForm = nearestSection.querySelector('form[action*="/cart/add"]');
      }
    }
    
    // Last resort fallback to first form
    if (!targetForm) {
      targetForm = $('form[action*="/cart/add"]');
    }
    
    const idInp    = targetForm?.querySelector('input[name="id"]') || $('input[name="id"]');
    const addBtn   = targetForm?.querySelector('[data-add-to-cart-text], #AddToCart, button[type="submit"], .p_a_t_c') || 
                    $('[data-add-to-cart-text], #AddToCart, button[type="submit"], .p_a_t_c');
    const buyWrap  = targetForm?.querySelector('.product-form__buttons') || $('.product-form__buttons');
    const notify   = targetForm?.querySelector('.product-signup') || $('.product-signup');

    if (!idInp) {
      return false;
    }
    
    const oldId = idInp.value;
    
    // Only update if the variant has actually changed
    if (oldId == v.id) {
      return true;
    }
    
    // Set flag to allow our own updates
    isUpdatingVariant = true;
    idInp.value = v.id;
    isUpdatingVariant = false;
    
    // Set the last selected variant ID to prevent external scripts from changing it
    lastSelectedVariantId = v.id;
    idInp.setAttribute('data-previous-value', v.id);
    
    idInp.dispatchEvent(new Event('change', { bubbles: true }));

    if (addBtn) {
      addBtn.disabled = !v.available;
      addBtn.textContent = v.available
        ? (addBtn.dataset.addToCartText || 'Add to cart')
        : 'Sold out';
    }

    if (buyWrap) {
      buyWrap.style.display = v.available ? 'block' : 'none';
    }
    if (notify)  {
      notify.style.display  = v.available ? 'none'  : 'block';
    }

    // Update price display
    updatePriceDisplay(v);

    // Update visual selection for all options
    updateVisualSelection();

    // Only dispatch variant change event for media gallery if this is a media-triggering change
    // Size changes should not affect the product image
    if (isMediaChange) {
      const productSection = document.querySelector('.js-product');
      if (productSection) {
        productSection.dispatchEvent(new CustomEvent('on:variant:change', {
          detail: {
            variant: v,
            product: window.productData
          },
          bubbles: true
        }));
      }
    }

    return true;
  };

  // Helper function to update visual selection for all options
  const updateVisualSelection = () => {
    // Update all option labels
    $$('input[data-option-name]').forEach(input => {
      const label = document.querySelector(`label[for="${input.id}"]`);
      if (label) {
        if (input.checked) {
          label.classList.add('is-selected');
        } else {
          label.classList.remove('is-selected');
        }
      }
    });
  };

  const updateSwatchUrls = () => {
    if (sizeIdx < 0) return;

    const sz = $$('input[type="radio"]:checked').find(r =>
      (r.dataset.optionName || r.name || '').toLowerCase().includes('size')
    )?.value;

    $$('.opt-label--swatch[href]').forEach(a => {
      try {
        const u = new URL(a.href, location.origin);
        sz ? u.searchParams.set('size', sz) : u.searchParams.delete('size');
        a.href = u.toString();
      } catch (_) {}
    });
  };

  /* ---------- combined products handling ---------- */
  const handleCombinedProductClick = (evt) => {
    const swatchLink = evt.target.closest('.opt-label--swatch[href]');
    if (!swatchLink) {
      return;
    }

    // Only handle elements that have an href (combined products)
    // Standard product swatches are labels without href
    if (!swatchLink.hasAttribute('href')) {
      return; // Let standard product labels work normally
    }

    // Check if this is a combined product (has href to different product)
    const currentProductUrl = window.location.pathname;
    const targetProductUrl = new URL(swatchLink.href, window.location.origin).pathname;
    
    if (targetProductUrl !== currentProductUrl) {
      // This is a combined product navigation - let it happen naturally
      return;
    }

    // Same product - handle as variant selection
    evt.preventDefault();
    
    const colorValue = swatchLink.dataset.value;
    if (!colorValue) return;

    // Find and check the corresponding radio button
    const radioInput = document.querySelector(`input[type="radio"][value="${colorValue}"]`);
    if (radioInput) {
      radioInput.checked = true;
      radioInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /* ---------- Comprehensive Cart Tracking ---------- */
  
  // Track all possible cart-related network requests
  const originalFetch = window.fetch;
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;
  
  // Override fetch to catch AJAX cart submissions
  window.fetch = function(...args) {
    const url = args[0];
    if (typeof url === 'string' && url.includes('/cart/add')) {
      // Check current variant ID
      const currentVariantId = $('input[name="id"]')?.value;
      const selectedVariant = variants.find(v => v.id == currentVariantId);
    }
    return originalFetch.apply(this, args);
  };
  
  // Override XMLHttpRequest to catch traditional AJAX
  XMLHttpRequest.prototype.open = function(method, url, ...args) {
    this._url = url;
    this._method = method;
    return originalXHROpen.apply(this, [method, url, ...args]);
  };
  
  XMLHttpRequest.prototype.send = function(data) {
    if (this._url && this._url.includes('/cart/add')) {
      // Check current variant ID
      const currentVariantId = $('input[name="id"]')?.value;
      const selectedVariant = variants.find(v => v.id == currentVariantId);
    }
    return originalXHRSend.apply(this, [data]);
  };

  /* ---------- initial run ---------- */
  
  const initialVariant = findVariant();
  applyVariant(initialVariant, false); // Don't trigger media gallery on initial load
  updateSwatchUrls();
  
  // Ensure visual selection is set on initial load
  updateVisualSelection();
  
  // Ensure price is updated on initial load
  updatePriceDisplay(initialVariant);

  // Monitor and prevent external scripts from changing variant ID when we've already selected the correct variant
  let lastSelectedVariantId = null;
  let isUpdatingVariant = false;
  const idInput = $('input[name="id"]');
  if (idInput) {
    // Check if the property has already been overridden to prevent the error
    const descriptor = Object.getOwnPropertyDescriptor(idInput, 'value');
    if (!descriptor || descriptor.configurable !== false) {
      // Override the value setter to catch all changes
      const originalValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      Object.defineProperty(idInput, 'value', {
        set: function(newValue) {
          const oldValue = this.getAttribute('value');
          
          // If we're currently updating the variant, allow it
          if (isUpdatingVariant) {
            originalValue.set.call(this, newValue);
            this.setAttribute('value', newValue);
            return;
          }
          
          // If this is an external change and we have a last selected variant
          // Only prevent changes if the new value is not a valid variant for the current selection
          if (lastSelectedVariantId && newValue !== lastSelectedVariantId) {
            // Check if the new value is a valid variant for the currently selected options
            const currentOptions = {};
            $$('input[type="radio"]:checked[data-option-name]').forEach(r => {
              const optionName = (r.dataset.optionName || r.name || '').toLowerCase();
              if (optionName.includes('color') || optionName.includes('colour')) {
                currentOptions.color = mapColorName(r.value);
              } else if (optionName.includes('size')) {
                currentOptions.size = r.value;
              }
            });
            
            // Find the variant that matches current selection
            let expectedVariant = null;
            if (currentOptions.color && currentOptions.size) {
              expectedVariant = variants.find(v => 
                v.options[colorIdx] === currentOptions.color && 
                v.options[sizeIdx] === currentOptions.size
              );
            }
            
            // If the new value doesn't match our expected variant, block it
            if (expectedVariant && newValue != expectedVariant.id) {
              // Don't allow the change
              return;
            }
          }
          originalValue.set.call(this, newValue);
          this.setAttribute('value', newValue);
        },
        get: function() {
          return originalValue.get.call(this);
        },
        configurable: false
      });
    }
  }

  /* ---------- listeners ---------- */
  
  document.addEventListener('change', e => {
    if (!e.target.matches('input[type="radio"]') ||
        e.target.classList.contains('color-option-input')) {
      return;
    }

    // Get current variant ID before processing
    const currentVariantId = $('input[name="id"]')?.value;

    // Find the specific form that should be updated
    // Radio buttons might not be inside the form, so find the closest cart form
    let changedRadioForm = e.target.closest('form[action*="/cart/add"]');
    
    // If radio button is not inside a cart form, find the closest one or use window.productData
    if (!changedRadioForm) {
      // Look for forms with the same product ID
      if (window.productData?.id) {
        const allForms = $$('form[action*="/cart/add"]');
        
        // Try to find form with matching product data or just use the first one for this product
        changedRadioForm = allForms.find(form => {
          const formProductId = form.querySelector('input[name="product-id"]')?.value || 
                               form.querySelector('[data-product-id]')?.dataset.productId;
          return formProductId == window.productData.id;
        }) || allForms[0]; // Fallback to first form
      }
      
      if (!changedRadioForm) {
        return;
      }
    }

    // Get all currently selected options within this specific form
    const selectedOptions = {};
    
    // Get all currently selected radio buttons (they might be outside the form)
    // but filter to only process ones that are relevant to this product/form context
    $$('input[type="radio"]:checked[data-option-name]').forEach(r => {
      const optionName = (r.dataset.optionName || r.name || '').toLowerCase();
      const optionPosition = parseInt(r.dataset.optionPosition) || 1;
      
      // Store the option value by its position (0-based index)
      const optionIndex = optionPosition - 1;
      selectedOptions[optionIndex] = r.value;
      
      // Also store by name for backward compatibility
      if (optionName.includes('color') || optionName.includes('colour')) {
        const originalValue = r.value;
        const mappedValue = mapColorName(r.value);
        selectedOptions.color = mappedValue;
      } else if (optionName.includes('size')) {
        selectedOptions.size = r.value;
      }
    });

    // Find variant that matches all selected options
    let selectedVariant = null;
    
    // First try to find exact match using option positions
    if (Object.keys(selectedOptions).length > 0) {
      selectedVariant = variants.find(v => {
        // Check if all selected options match the variant
        return Object.entries(selectedOptions).every(([optionIndex, value]) => {
          // Skip color/size keys (they're for backward compatibility)
          if (optionIndex === 'color' || optionIndex === 'size') return true;
          
          const index = parseInt(optionIndex);
          return v.options[index] === value;
        });
      });
    }
    
    // Fallback to color/size logic for backward compatibility
    if (!selectedVariant && selectedOptions.color && selectedOptions.size && colorIdx >= 0 && sizeIdx >= 0) {
      selectedVariant = variants.find(v => 
        v.options[colorIdx] === selectedOptions.color && 
        v.options[sizeIdx] === selectedOptions.size
      );
    } else if (!selectedVariant && selectedOptions.color && colorIdx >= 0) {
      selectedVariant = variants.find(v => v.options[colorIdx] === selectedOptions.color && v.available);
      if (!selectedVariant) {
        selectedVariant = variants.find(v => v.options[colorIdx] === selectedOptions.color);
      }
    } else if (!selectedVariant && selectedOptions.size && sizeIdx >= 0) {
      selectedVariant = variants.find(v => v.options[sizeIdx] === selectedOptions.size && v.available);
      if (!selectedVariant) {
        selectedVariant = variants.find(v => v.options[sizeIdx] === selectedOptions.size);
      }
    }

    if (!selectedVariant) {
      // Fallback to first available variant
      selectedVariant = variants.find(v => v.available) || variants[0];
    }

    // Check if the selected variant is the same as current
    if (currentVariantId == selectedVariant.id) {
      return;
    }

    // Check if this is a media-triggering change (first option like color, style, etc.)
    const optionName = (e.target.dataset.optionName || e.target.name || '').toLowerCase();
    const optionPosition = parseInt(e.target.dataset.optionPosition) || 1;
    const isMediaChange = optionName.includes('color') || optionName.includes('colour') || optionPosition === 1;
    
    applyVariant(selectedVariant, isMediaChange);
    updateSwatchUrls();
  });

  // Handle color swatch clicks for combined products
  document.addEventListener('click', handleCombinedProductClick);

  // Enhanced cart form submission logging
  $$('form[action*="/cart/add"]').forEach((f, i) => {
    f.addEventListener('submit', e => {
      // Force update the variant ID to match current selection before submission
      const currentSelection = {};
      $$('input[type="radio"]:checked[data-option-name]').forEach(r => {
        const optionName = (r.dataset.optionName || r.name || '').toLowerCase();
        const optionPosition = parseInt(r.dataset.optionPosition) || 1;
        const optionIndex = optionPosition - 1;
        currentSelection[optionIndex] = r.value;
        
        if (optionName.includes('color') || optionName.includes('colour')) {
          currentSelection.color = mapColorName(r.value);
        } else if (optionName.includes('size')) {
          currentSelection.size = r.value;
        }
      });
      
      // Find the correct variant for current selection
      let correctVariant = null;
      if (Object.keys(currentSelection).length > 0) {
        correctVariant = variants.find(v => {
          return Object.entries(currentSelection).every(([optionIndex, value]) => {
            if (optionIndex === 'color' || optionIndex === 'size') return true;
            const index = parseInt(optionIndex);
            return v.options[index] === value;
          });
        });
        
        if (!correctVariant && currentSelection.color && currentSelection.size && colorIdx >= 0 && sizeIdx >= 0) {
          correctVariant = variants.find(v => 
            v.options[colorIdx] === currentSelection.color && 
            v.options[sizeIdx] === currentSelection.size
          );
        } else if (!correctVariant && currentSelection.size && sizeIdx >= 0) {
          correctVariant = variants.find(v => v.options[sizeIdx] === currentSelection.size);
        }
      }
      
      // Force update the hidden input to the correct variant
      const variantIdInput = f.querySelector('input[name="id"]');
      if (correctVariant && variantIdInput) {
        isUpdatingVariant = true;
        variantIdInput.value = correctVariant.id;
        isUpdatingVariant = false;
      }
      
      const variantId = variantIdInput?.value;
      const selectedVariant = variants.find(v => v.id == variantId);
      
      if (!applyVariant(findVariant(), false)) { // Don't trigger media gallery on form submit
        e.preventDefault();
      }
    });
  });

  // Also listen for click events on any add to cart buttons
  document.addEventListener('click', (e) => {
    const button = e.target.closest('button, input[type="submit"], .btn, [data-add-to-cart]');
    if (button && (
      button.textContent?.toLowerCase().includes('add to cart') ||
      button.classList.contains('btn-product_addtocart') ||
      button.hasAttribute('data-add-to-cart') ||
      button.form?.action?.includes('/cart/add')
    )) {
      const currentVariantId = $('input[name="id"]')?.value;
      const selectedVariant = variants.find(v => v.id == currentVariantId);
    }
  });
});
